import { isDelisted } from "@/lib/delisted";
import { toast } from "sonner";
import { matchProductTypeAlias } from "./searchTypes";


// Shopify API Configuration
const SHOPIFY_API_VERSION = '2025-07';
const SHOPIFY_STORE_PERMANENT_DOMAIN = '9ab1f9-2.myshopify.com';
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
const SHOPIFY_STOREFRONT_TOKEN = '6d1a1a31b3dbd4081791e6e7446c7b98';

// Shared query focusing on core furniture, excluding heavy non-furniture categories.
//
// Catalog counts measured against the live Storefront API on 2026-08-18:
//   total catalog = 22,349 · Area Rugs = 19 · Sculptures = 1,372
// Area Rugs used to be excluded on the basis of a stale count of ~4,970; at 19
// products it adds no meaningful weight, so it is now included here (which also
// lets rugs surface in All Products / Best Sellers / New Arrivals / Sale).
// Sculptures (1,372) stays excluded deliberately — it is tabletop decor, not
// furniture, and has its own dedicated category page.
export const FURNITURE_QUERY = 'product_type:Sofas OR product_type:Sectionals OR product_type:"Accent Chairs" OR product_type:"Dining Tables" OR product_type:"Dining Chairs" OR product_type:"Standard Beds" OR product_type:Dressers OR product_type:Desks OR product_type:"Office Chairs" OR product_type:"Coffee Tables" OR product_type:"Console Tables" OR product_type:Recliners OR product_type:"TV Stands" OR product_type:Bookcases OR product_type:"Bar Chairs" OR product_type:Headboards OR product_type:Benches OR product_type:Chandeliers OR product_type:"Floor Lamps" OR product_type:Sideboards OR product_type:"Outdoor Sofas" OR product_type:"Outdoor Tables" OR product_type:"Outdoor Chairs" OR product_type:"Bathroom Vanities" OR product_type:Ottomans OR product_type:"Love Seats" OR product_type:"Dining Sets" OR product_type:Cabinets OR product_type:Fireplaces OR product_type:"Table Lamps" OR product_type:"Bunk Beds" OR product_type:"Loft Beds" OR product_type:"Area Rugs"';

// Extended query that additionally includes Mirrors plus the soft-goods / lighting
// / bath product types that have their own category pages, so All Products covers them.
export const FURNITURE_QUERY_EXTENDED = FURNITURE_QUERY +
  ' OR product_type:Mirrors' +
  ' OR product_type:"Throws & Blankets"' +
  ' OR product_type:"Nightstands & Bedside Tables"' +
  ' OR product_type:Lampshades' +
  ' OR product_type:"Pendants-Hanging Lights"' +
  ' OR product_type:Doormats' +
  ' OR product_type:"Wall Lighting"' +
  ' OR product_type:"Christmas Ornaments"' +
  ' OR product_type:"Outdoor Hanging Lights"' +
  ' OR product_type:"Shower Benches"' +
  ' OR product_type:"Peel and Stick Tiles"' +
  ' OR product_type:"Down Comforters and Duvets"' +
  ' OR product_type:"Bath Rugs"' +
  ' OR product_type:"Bedroom Furniture Sets"' +
  ' OR product_type:Candles' +
  ' OR product_type:"Vanity Tables"' +
  // Furniture types with their own category page that All Products never
  // listed. Deliberately not every type: the page makes one request per type
  // before it shows anything (62 types measured ~4 s slower than 49).
  ' OR product_type:"End-Side Tables"' +
  ' OR product_type:"Room Dividers"' +
  ' OR product_type:"Shoe Racks"' +
  ' OR product_type:"Pantry Cabinets"' +
  ' OR product_type:"Kitchen Carts-Islands"' +
  ' OR product_type:"Bathroom Storage & Organization"' +
  ' OR product_type:"Foam, Spring And Hybrid Mattresses"';

// ─────────────────────────────────────────────────────────────
// Availability filtering
//
// Measured against the live Storefront API (2026-08-25):
//   • products(query: '... AND available_for_sale:true')  → works, in-stock only ✅
//   • search(query: 'available_for_sale:true')            → totalCount 0, BROKEN ❌
//
// So the filter is applied to the `products` connection only (fetchProducts /
// fetchProductsWithPagination). It must NEVER be added to fetchProductCount,
// which runs on the `search` connection and would report 0 everywhere.
//
// Out-of-stock products stay reachable by direct URL — the PDP handles
// unavailable variants with a Back-in-Stock form. This only stops listings
// from surfacing dead ends.
const AVAILABILITY_FILTER = 'available_for_sale:true';

// ─────────────────────────────────────────────────────────────
// Zero-price filtering
//
// Measured against the live Storefront API (2026-09-12): 659 products were
// priced $0.00 and every one was purchasable, including dining tables and a
// mattress. `variants.price:>0` IS honoured on the `products` connection
// (`variants.price:>0 AND variants.price:<1` → 0 results) but is silently
// IGNORED by the `search` connection, exactly like `available_for_sale:`.
// So: filter in the query on `products`, and filter in code after `search`.
const NONZERO_PRICE_FILTER = 'variants.price:>0';

/** The listing guard: in stock AND actually priced. */
const LISTING_FILTERS = `${AVAILABILITY_FILTER} AND ${NONZERO_PRICE_FILTER}`;

function withAvailability(query?: string | null): string {
  if (!query) return LISTING_FILTERS;
  const filters = [
    query.includes('available_for_sale:') ? null : AVAILABILITY_FILTER,
    query.includes('variants.price:') ? null : NONZERO_PRICE_FILTER,
  ].filter(Boolean);
  if (filters.length === 0) return query;
  // Parenthesise: AND binds tighter than OR in Shopify search syntax, so an
  // unwrapped OR-chain would only have the filter applied to its last term.
  return `(${query}) AND ${filters.join(' AND ')}`;
}

/** `variants.price:>0` never reaches the `search` connection, so drop $0 rows here. */
function dropZeroPriced<T extends { priceRange?: { minVariantPrice: { amount: string } } }>(
  rows: T[],
): T[] {
  return rows.filter((r) => parseFloat(r?.priceRange?.minVariantPrice?.amount || '0') > 0);
}

/** Also used where a listing pool is assembled outside a products query. */
export function hasSellablePrice(amount?: string | number | null): boolean {
  return parseFloat(String(amount ?? '0')) > 0;
}


export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    createdAt?: string;
    productType?: string;
    /**
     * Product-level stock flag from Shopify. `quantityAvailable` is NOT
     * available to this Storefront token (no unauthenticated_read_product_inventory
     * scope), so grids can only ever say "in stock" or "Sold out" — never a count.
     */
    availableForSale?: boolean;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    compareAtPriceRange?: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
          width?: number | null;
          height?: number | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          compareAtPrice?: {
            amount: string;
            currencyCode: string;
          } | null;
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
          /** The variant's own photo. Only the product-page query asks for it. */
          image?: { url: string; altText: string | null } | null;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
  };
}

// Storefront API helper function. `signal` lets a caller give up on a request
// that gets no answer (the cart does, see cartRequest in cartStore).
export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}, signal?: AbortSignal) {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN
    },
    body: JSON.stringify({
      query,
      variables,
    }),
    signal,
  });

  if (response.status === 402) {
    toast.error("Shopify: Payment required", {
      description: "Shopify API access requires an active Shopify billing plan. Visit https://admin.shopify.com to upgrade.",
    });
    return null;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  
  if (data.errors) {
    throw new Error(`Error calling Shopify: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  return data;
}

// GraphQL Queries
// What a listing reads from a `products` connection. Shared by the single query
// and the aliased one (fetchProductSlices) so every grid gets the same fields.
const PRODUCT_LIST_FIELDS = `
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          description
          handle
          createdAt
          productType
          availableForSale
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
                width
                height
              }
            }
          }
          variants(first: 20) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
        }
      }
`;

// The few fields filter options and the price scale are read from. Sixty
// products of each of Living Room's 16 types are 240 KB of JSON this way
// (20 KB gzipped); as full cards they were 3.2 MB.
const PRODUCT_FACET_FIELDS = `
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          productType
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          options {
            name
            values
          }
        }
      }
`;

const PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $query: String, $after: String, $sortKey: ProductSortKeys, $reverse: Boolean) {
    products(first: $first, query: $query, after: $after, sortKey: $sortKey, reverse: $reverse) {${PRODUCT_LIST_FIELDS}    }
  }
`;

const COLLECTIONS_QUERY = `
  query GetCollections($first: Int!) {
    collections(first: $first) {
      edges {
        node {
          id
          title
          handle
          description
          image {
            url
            altText
          }
          products(first: 1) {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;

const SEARCH_QUERY = `
  query SearchProducts($query: String!, $first: Int!) {
    search(query: $query, first: $first, types: [PRODUCT]) {
      edges {
        node {
          ... on Product {
            id
            title
            handle
            productType
            availableForSale
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 1) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
          }
        }
      }
    }
  }
`;

const PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      description
      handle
      productType
      availableForSale
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      compareAtPriceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 10) {
        edges {
          node {
            url
            altText
          }
        }
      }
      # Every variant, so each option value has a button (a 26-letter monogram
      # throw showed only A to T). The opening variant is still picked from the
      # first 20, as the build does (defaultVariant in src/lib/variants.ts).
      variants(first: 100) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
            image {
              url
              altText
            }
          }
        }
      }
      options {
        name
        values
      }
    }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// API Functions
export interface FetchProductsResult {
  products: ShopifyProduct[];
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
}

/** A product as PRODUCT_FACET_FIELDS returns it; every full product is one too. */
export interface FacetProduct {
  node: Pick<ShopifyProduct["node"], "id" | "title" | "productType" | "priceRange" | "options">;
}

export async function fetchProducts(
  first: number = 20,
  query?: string,
  options?: { after?: string; sortKey?: string; reverse?: boolean; includeUnavailable?: boolean }
): Promise<ShopifyProduct[]> {
  const data = await storefrontApiRequest(PRODUCTS_QUERY, {
    first,
    query: options?.includeUnavailable ? (query || null) : withAvailability(query),
    after: options?.after || null,
    sortKey: options?.sortKey || null,
    reverse: options?.reverse ?? null,
  });
  if (!data) return [];
  return data.data.products.edges.filter((e: ShopifyProduct) => !isDelisted(e.node.title));
}

// Full search-results page. The `search` connection ranks by relevance and
// matches "8x10" against titles that spell it 8' X 10'. The `products`
// connection used before has no relevance ranking: "sofa" led with floor
// lamps, "coffee table" with model boats, and "rug 8x10" found nothing.
// Sold-out products are hidden with unavailableProducts: HIDE (this connection
// ignores `available_for_sale:`), and $0 rows are dropped in code.
const SEARCH_PAGE_QUERY = `
  query SearchPage($query: String!, $first: Int!, $after: String) {
    search(query: $query, first: $first, after: $after, types: [PRODUCT], unavailableProducts: HIDE, sortKey: RELEVANCE) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ... on Product {
          id
          title
          description
          handle
          createdAt
          productType
          availableForSale
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
                width
                height
              }
            }
          }
          variants(first: 20) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
          }
        }
      }
    }
  }
`;

export async function searchProductsWithPagination(first: number, query: string, after?: string | null): Promise<FetchProductsResult> {
  const data = await storefrontApiRequest(SEARCH_PAGE_QUERY, { query, first, after: after || null });
  if (!data?.data?.search) throw new Error("Search results unavailable");
  const nodes = dropZeroPriced<ShopifyProduct["node"]>(
    data.data.search.edges.map((e: { node: ShopifyProduct["node"] }) => e.node),
  ).filter((n) => n?.id && !isDelisted(n.title));
  return { products: nodes.map((node) => ({ node })), pageInfo: data.data.search.pageInfo };
}

export async function fetchProductsWithPagination(
  first: number = 20,
  query?: string,
  after?: string,
  options?: { sortKey?: string; reverse?: boolean; includeUnavailable?: boolean }
): Promise<FetchProductsResult> {
  const data = await storefrontApiRequest(PRODUCTS_QUERY, {
    first,
    query: options?.includeUnavailable ? (query || null) : withAvailability(query),
    after: after || null,
    sortKey: options?.sortKey || null,
    reverse: options?.reverse ?? null,
  });
  if (!data) return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };

  return {
    products: data.data.products.edges.filter((e: ShopifyProduct) => !isDelisted(e.node.title)),
    pageInfo: data.data.products.pageInfo,
  };
}

/**
 * Several listings in one round trip: each slice becomes its own aliased
 * `products` connection. A multi-type category page used to send one request
 * per product type (16 for Living Room, 22 for Bedroom) before its first card.
 * Results come back in slice order.
 */
async function fetchSlices<T extends FacetProduct>(
  fields: string,
  slices: Array<{ query: string; first: number; after?: string | null }>,
  options?: { sortKey?: string; reverse?: boolean }
): Promise<Array<{ products: T[]; pageInfo: FetchProductsResult["pageInfo"] }>> {
  if (slices.length === 0) return [];
  const variables: Record<string, unknown> = {
    sortKey: options?.sortKey || null,
    reverse: options?.reverse ?? null,
  };
  const declarations: string[] = [];
  const connections: string[] = [];
  slices.forEach((slice, i) => {
    variables[`q${i}`] = withAvailability(slice.query);
    variables[`a${i}`] = slice.after || null;
    declarations.push(`$q${i}: String, $a${i}: String`);
    // 250 is the most one connection returns.
    const first = Math.max(1, Math.min(250, Math.floor(slice.first)));
    connections.push(`s${i}: products(first: ${first}, query: $q${i}, after: $a${i}, sortKey: $sortKey, reverse: $reverse) { ...ProductListFields }`);
  });
  const data = await storefrontApiRequest(
    `
  query GetProductSlices(${declarations.join(", ")}, $sortKey: ProductSortKeys, $reverse: Boolean) {
    ${connections.join("\n    ")}
  }
  fragment ProductListFields on ProductConnection {${fields}  }
`,
    variables,
  );

  return slices.map((_, i) => {
    const connection = data?.data?.[`s${i}`];
    if (!connection) return { products: [], pageInfo: { hasNextPage: false, endCursor: null } };
    return {
      products: connection.edges.filter((e: T) => !isDelisted(e.node.title)),
      pageInfo: connection.pageInfo,
    };
  });
}

export function fetchProductSlices(
  slices: Array<{ query: string; first: number; after?: string | null }>,
  options?: { sortKey?: string; reverse?: boolean }
): Promise<FetchProductsResult[]> {
  return fetchSlices<ShopifyProduct>(PRODUCT_LIST_FIELDS, slices, options);
}

/** The same listings, read only as far as filters need them (PRODUCT_FACET_FIELDS). */
export function fetchFacetSlices(
  slices: Array<{ query: string; first: number; after?: string | null }>,
  options?: { sortKey?: string; reverse?: boolean }
) {
  return fetchSlices<FacetProduct>(PRODUCT_FACET_FIELDS, slices, options);
}

export interface ShopifyCollection {
  node: {
    id: string;
    title: string;
    handle: string;
    description: string;
    image: { url: string; altText: string | null } | null;
    products: { edges: Array<{ node: { id: string } }> };
  };
}

export async function fetchCollections(first: number = 20): Promise<ShopifyCollection[]> {
  const data = await storefrontApiRequest(COLLECTIONS_QUERY, { first });
  if (!data) return [];
  return data.data.collections.edges;
}

export interface SearchResult {
  id: string;
  title: string;
  handle: string;
  /** The supplier's type: display names need it to name the product correctly. */
  productType?: string;
  /** Product-level stock flag. No inventory counts are available to this token. */
  availableForSale?: boolean;
  priceRange?: {
    minVariantPrice: { amount: string; currencyCode: string };
    /** Above the minimum when variants differ in price: the row says "From". */
    maxVariantPrice?: { amount: string; currencyCode: string };
  };
  images?: {
    edges: Array<{ node: { url: string; altText: string | null } }>;
  };
}

async function rawSearch(query: string, first: number): Promise<SearchResult[]> {
  const data = await storefrontApiRequest(SEARCH_QUERY, { query, first });
  if (!data) return [];
  // The `search` connection ignores `variants.price:` filters — filter in code.
  return dropZeroPriced<SearchResult>(data.data.search.edges.map((e: { node: SearchResult }) => e.node)).filter((r) => !isDelisted(r.title));
}

// The `search` connection returns 0 for `available_for_sale:` filters (measured),
// so scoped in-stock lookups must run on the `products` connection instead.
const PRODUCTS_SEARCH_QUERY = `
  query ProductsSearch($query: String!, $first: Int!) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          handle
          productType
          availableForSale
          priceRange { minVariantPrice { amount currencyCode } maxVariantPrice { amount currencyCode } }
          images(first: 1) { edges { node { url altText } } }
        }
      }
    }
  }
`;

async function rawProductsSearch(query: string, first: number): Promise<SearchResult[]> {
  const data = await storefrontApiRequest(PRODUCTS_SEARCH_QUERY, { query, first });
  if (!data) return [];
  return dropZeroPriced<SearchResult>(data.data.products.edges.map((e: { node: SearchResult }) => e.node)).filter((r) => !isDelisted(r.title));
}

/**
 * Product search with product-type scoping.
 *
 * Shopify's loose `query:` full-text matches title/description/tags with no
 * product-type weighting, so "coffee table" used to return a glass table VASE
 * first. When the query contains a known product-type alias (longest match
 * wins, so "coffee table" beats "table"), we run a scoped query first and put
 * those results ahead of the general results, de-duplicated by handle.
 */
export async function searchProducts(query: string, first: number = 8): Promise<SearchResult[]> {
  const match = matchProductTypeAlias(query);
  if (!match) return rawSearch(query, first);

  const leftover = query
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(new RegExp(`\\b${match.alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g'), ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Scoped queries are in-stock only: promoting a whole product type to the top of
  // search must never lead with dead stock (every Area Rug is out of stock, so a
  // rug scope would surface unbuyable items). The general query below stays
  // unfiltered so nothing disappears from search.
  const typeFilter = `product_type:"${match.productType}" AND ${LISTING_FILTERS}`;
  let scoped: SearchResult[] = [];
  try {
    if (leftover) scoped = await rawProductsSearch(`${typeFilter} AND ${leftover}`, first);
    if (scoped.length === 0) scoped = await rawProductsSearch(typeFilter, first);
  } catch {
    scoped = [];
  }



  const general = await rawSearch(query, first);

  const seen = new Set<string>();
  const merged: SearchResult[] = [];
  for (const r of [...scoped, ...general]) {
    if (!r || seen.has(r.handle)) continue;
    seen.add(r.handle);
    merged.push(r);
  }
  return merged.slice(0, first);
}


export async function fetchProductByHandle(handle: string): Promise<ShopifyProduct['node'] | null> {
  const data = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, { handle });
  // No answer (HTTP 402) is a failure, not a deleted product: only a successful
  // response with productByHandle null may show "no longer available".
  if (!data) throw new Error("Storefront API unavailable");
  return data.data.productByHandle;
}

export async function createStorefrontCheckout(items: Array<{ variantId: string; quantity: number }>): Promise<string> {
  const lines = items.map(item => ({
    quantity: item.quantity,
    merchandiseId: item.variantId,
  }));

  const cartData = await storefrontApiRequest(CART_CREATE_MUTATION, {
    input: { lines },
  });

  if (!cartData) {
    throw new Error('Failed to create checkout');
  }

  if (cartData.data.cartCreate.userErrors.length > 0) {
    throw new Error(`Cart creation failed: ${cartData.data.cartCreate.userErrors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  const cart = cartData.data.cartCreate.cart;
  
  if (!cart.checkoutUrl) {
    throw new Error('No checkout URL returned from Shopify');
  }

  const url = new URL(cart.checkoutUrl);
  url.searchParams.set('channel', 'online_store');
  return url.toString();
}

// Fetch total product count using search query (supports totalCount)
const PRODUCT_COUNT_QUERY = `
  query GetProductCount($query: String!) {
    search(query: $query, types: PRODUCT, first: 1) {
      totalCount
    }
  }
`;

export async function fetchProductCount(query?: string): Promise<number | null> {
  try {
    const searchQuery = query || "*";
    const data = await storefrontApiRequest(PRODUCT_COUNT_QUERY, { query: searchQuery });
    if (!data) return null;
    return data.data.search.totalCount;
  } catch {
    return null;
  }
}

// Helper to resize Shopify CDN images by appending width parameter
export function getShopifyImageUrl(url: string, width: number): string {
  if (!url || !url.includes('cdn.shopify.com')) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch {
    return url;
  }
}

// Helper to format price
export function formatPrice(amount: string, currencyCode: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  }).format(parseFloat(amount));
}
